import axios from 'axios';
import React, { useState } from 'react';

const ProductCatalogStep = ({ productCatalog, petPersonalization, setPetPersonalization, onAddToCart, onNext, onBack }) => {
  // State for individual product personalizations
  const [productPersonalizations, setProductPersonalizations] = useState({});

  // Helper function to get personalization for specific product
  const getPersonalization = (productKey) => {
    return productPersonalizations[productKey] || '';
  };

  // Helper function to set personalization for specific product
  const setPersonalizationFor = (productKey, value) => {
    setProductPersonalizations(prev => ({
      ...prev,
      [productKey]: value
    }));
  };

  // Organize urns by categories for better UX
  const organizeUrnsByCategory = (urns) => {
    const organized = {
      'Ceramic Urns': [],
      'Bamboo Urns': [],
      'Concrete Urns': [],
      'Frame Urns': [],
      'Memorial Boxes': [],
      'Pouch & Candle': [],
      'Exotic Pet Urns': []
    };

    Object.entries(urns || {}).forEach(([category, data]) => {
      const options = data.options || [];
      
      if (category.includes('Ceramic')) {
        organized['Ceramic Urns'].push(...options.map(urn => ({ ...urn, originalCategory: category })));
      } else if (category.includes('Bamboo')) {
        organized['Bamboo Urns'].push(...options.map(urn => ({ ...urn, originalCategory: category })));
      } else if (category.includes('Concrete')) {
        organized['Concrete Urns'].push(...options.map(urn => ({ ...urn, originalCategory: category })));
      } else if (category.includes('Frame')) {
        organized['Frame Urns'].push(...options.map(urn => ({ ...urn, originalCategory: category })));
      } else if (category.includes('MEMORIES BOX')) {
        organized['Memorial Boxes'].push(...options.map(urn => ({ ...urn, originalCategory: category })));
      } else if (category.includes('Pouch')) {
        organized['Pouch & Candle'].push(...options.map(urn => ({ ...urn, originalCategory: category })));
      } else if (category.includes('Exotic')) {
        organized['Exotic Pet Urns'].push(...options.map(urn => ({ ...urn, originalCategory: category })));
      }
    });

    // Remove empty categories
    return Object.fromEntries(
      Object.entries(organized).filter(([, items]) => items.length > 0)
    );
  };

  const organizedUrns = organizeUrnsByCategory(productCatalog.urns);

  return (
    <div className="max-w-6xl mx-auto">
      <h3 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">Select Memorial Products</h3>
      
      {/* Global Pet Personalization Helper */}
      <div className="mb-4 md:mb-8 p-3 md:p-4 bg-blue-50 rounded-lg">
        <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
          Default Pet's Name (can be customized per product)
        </label>
        <input
          type="text"
          value={petPersonalization}
          onChange={(e) => setPetPersonalization(e.target.value)}
          className="w-full px-2 md:px-3 py-1.5 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm md:text-base"
          placeholder="Enter your pet's name as default for all items"
        />
        <p className="text-xs text-gray-500 mt-1">
          This name will be used as default, but you can customize it for each individual product below.
        </p>
      </div>

      {/* Product Categories - Mobile Optimized */}
      <div className="space-y-6 md:space-y-12">
        {/* Organized Urns by Category */}
        {Object.entries(organizedUrns).map(([categoryName, urns]) => (
          <div key={categoryName}>
            <h4 className="text-base md:text-lg font-medium mb-2 md:mb-4 text-indigo-700 border-b border-indigo-200 pb-2">
              {categoryName}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
              {urns.map(urn => {
                const productKey = `urn-${urn.name.replace(/[^a-zA-Z0-9]/g, '')}`;
                return (
                  <div key={urn.name} className="border rounded-lg p-3 md:p-4 hover:shadow-lg transition-all duration-200 bg-white">
                    <h5 className="font-medium text-gray-900 mb-1 md:mb-2 text-sm md:text-base">{urn.name}</h5>
                    <p className="text-xs md:text-sm text-gray-600 mb-1 md:mb-2">Category: {urn.originalCategory}</p>
                    <p className="text-base md:text-lg font-semibold text-indigo-600 mb-2 md:mb-4">${urn.price}</p>
                    
                    {/* Individual Pet Personalization - Compact */}
                    <div className="mb-2 md:mb-4">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Pet's Name:
                      </label>
                      <input
                        type="text"
                        value={getPersonalization(productKey)}
                        onChange={(e) => setPersonalizationFor(productKey, e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs md:text-sm focus:ring-1 focus:ring-indigo-500 focus:border-transparent"
                        placeholder={petPersonalization || "Enter pet's name"}
                      />
                    </div>
                    
                    <button
                      onClick={() => onAddToCart(urn, 'urn', getPersonalization(productKey))}
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-lg text-xs md:text-sm font-medium transition duration-200"
                    >
                      Add to Cart
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Memorial Jewelry - Compact */}
        <div>
          <h4 className="text-base md:text-lg font-medium mb-2 md:mb-4 text-purple-700 border-b border-purple-200 pb-2">Memorial Jewelry</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
            {(productCatalog.jewelry || []).map(item => {
              const productKey = `jewelry-${item.name.replace(/[^a-zA-Z0-9]/g, '')}`;
              return (
                <div key={item.name} className="border rounded-lg p-3 md:p-4 hover:shadow-lg transition-all duration-200 bg-white">
                  <h5 className="font-medium text-gray-900 mb-1 md:mb-2 text-sm md:text-base">{item.name}</h5>
                  <p className="text-base md:text-lg font-semibold text-purple-600 mb-2 md:mb-4">${item.price}</p>
                  
                  {/* Individual Pet Personalization */}
                  <div className="mb-2 md:mb-4">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Pet's Name:
                    </label>
                    <input
                      type="text"
                      value={getPersonalization(productKey)}
                      onChange={(e) => setPersonalizationFor(productKey, e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs md:text-sm focus:ring-1 focus:ring-purple-500 focus:border-transparent"
                      placeholder={petPersonalization || "Enter pet's name"}
                    />
                  </div>
                  
                  <button
                    onClick={() => onAddToCart(item, 'jewelry', getPersonalization(productKey))}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-lg text-xs md:text-sm font-medium transition duration-200"
                  >
                    Add to Cart
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Memorial Keepsakes - Compact */}
        <div>
          <h4 className="text-base md:text-lg font-medium mb-2 md:mb-4 text-green-700 border-b border-green-200 pb-2">Memorial Keepsakes</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
            {(productCatalog.keepsakes || []).map(item => {
              const productKey = `keepsake-${item.name.replace(/[^a-zA-Z0-9]/g, '')}`;
              return (
                <div key={item.name} className="border rounded-lg p-3 md:p-4 hover:shadow-lg transition-all duration-200 bg-white">
                  <h5 className="font-medium text-gray-900 mb-1 md:mb-2 text-sm md:text-base">{item.name}</h5>
                  <p className="text-base md:text-lg font-semibold text-green-600 mb-2 md:mb-4">${item.price}</p>
                  
                  {/* Individual Pet Personalization */}
                  <div className="mb-2 md:mb-4">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Pet's Name:
                    </label>
                    <input
                      type="text"
                      value={getPersonalization(productKey)}
                      onChange={(e) => setPersonalizationFor(productKey, e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs md:text-sm focus:ring-1 focus:ring-green-500 focus:border-transparent"
                      placeholder={petPersonalization || "Enter pet's name"}
                    />
                  </div>
                  
                  <button
                    onClick={() => onAddToCart(item, 'keepsake', getPersonalization(productKey))}
                    className="w-full bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-lg text-xs md:text-sm font-medium transition duration-200"
                  >
                    Add to Cart
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex justify-between mt-8 md:mt-12">
        <button
          onClick={onBack}
          className="px-4 md:px-6 py-2 md:py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm md:text-base"
        >
          ← Back to Customer Info
        </button>
        <button
          onClick={onNext}
          className="px-4 md:px-6 py-2 md:py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium text-sm md:text-base"
        >
          Review Cart →
        </button>
      </div>
    </div>
  );
};

// Cart Review Step Component

export default ProductCatalogStep;
